import type {
  BridgePrinterStatus,
  RetrySafety,
} from '@/interfaces/print-bridge-protocol';
import type { IPrintJob } from '@/interfaces/printing.interfaces';

/**
 * Pure logic for the print-health store (no Vue imports — fully
 * unit-testable): failure-entry dedupe, retryable-vs-verify classification,
 * acknowledgment pruning, printer-health mapping/diffing and the DEMORADA
 * (late comanda) age computation.
 */

/** Alarm repeat period while unacknowledged failures exist. */
export const PRINT_FAIL_ALARM_INTERVAL_MS = 30_000;
/** Success counter chip fades out after this long without a new print. */
export const SUCCESS_CHIP_HIDE_MS = 5_000;
/** Success counter display cap ("99+"). */
export const SUCCESS_CHIP_MAX_DISPLAY = 99;
/** Assigned-printer health poll period (native poller; bridge TCP probes). */
export const PRINTER_HEALTH_POLL_INTERVAL_MS = 30_000;
/** Items older than this get the DEMORADA header on their comanda. */
export const DELAYED_PRINT_THRESHOLD_MS = 10 * 60 * 1000;

/**
 * 'verify' = the document MAY have physically printed (bytes possibly
 * reached the printer) — staff must check the paper before re-printing.
 * 'retryable' = provably nothing printed; catch-up will auto-reprint.
 */
export type PrintFailureKind = 'retryable' | 'verify';

/** Platform-agnostic printer status consumed by widget + recovery watcher. */
export type UnifiedPrinterStatus = 'online' | 'offline' | 'unknown';

export interface IPrintFailureEntry {
  /** Dedupe key: printerType + station + order-id set (see failureKeyOf). */
  key: string;
  kind: PrintFailureKind;
  printerType: IPrintJob['printerType'];
  comandaStationId?: number;
  /** Orders still covered by this entry (group entries shrink per-order). */
  orderIds: number[];
  title: string;
  errorMessage?: string;
  /** Failed attempts collapsed into this entry. */
  count: number;
  firstAt: number;
  lastAt: number;
}

/**
 * Mirror of hasUnsafeRetrySafety semantics: ONLY 'unsafe' | 'unknown' are
 * verify-kind. 'safe' or ABSENT retrySafety (plain Errors) is retryable —
 * catch-up auto-reprints those today, so labeling them "verify on paper"
 * would mislead staff into double-printing.
 */
export function failureKindOf(
  retrySafety: RetrySafety | undefined
): PrintFailureKind {
  return retrySafety === 'unsafe' || retrySafety === 'unknown'
    ? 'verify'
    : 'retryable';
}

function orderIdsOf(job: IPrintJob): number[] {
  if (job.metadata?.orderIds?.length) return [...job.metadata.orderIds];
  if (typeof job.metadata?.orderId === 'number') return [job.metadata.orderId];
  return [];
}

/**
 * Dedupe key for a failed job: same printer target + same order-id set
 * collapse into one entry (retries bump count instead of re-arming the
 * alarm). Group jobs key on the SORTED id set; jobs without any order id
 * fall back to the title.
 */
export function failureKeyOf(job: IPrintJob): string {
  const ids = orderIdsOf(job);
  const target = ids.length
    ? ids
        .slice()
        .sort((a, b) => a - b)
        .join(',')
    : `t:${job.title}`;
  return `${job.printerType}:${job.comandaStationId ?? ''}:${target}`;
}

function sameTarget(entry: IPrintFailureEntry, job: IPrintJob): boolean {
  return (
    entry.printerType === job.printerType &&
    (entry.comandaStationId ?? null) === (job.comandaStationId ?? null)
  );
}

/**
 * Reducer: applies one print job (success or failure) to the failure list.
 * A NEW entry re-arms the alarm implicitly: its key is not in ackedKeys, so
 * hasUnackedFailures flips true (retries of a known key stay acked).
 *
 * - failed job → upsert by dedupe key (existing entry: count/lastAt bump,
 *   kind can only escalate retryable → verify: once bytes may have reached
 *   the printer, a later clean failure doesn't un-print them);
 * - unknown job (post-send DLE-EOT status report) → same upsert with kind
 *   FORCED to 'verify': the document may have printed, staff must check the
 *   paper — silencing it would stop alarming paper-out incidents that alarm
 *   today;
 * - printed job (ONLY 'printed' — never any other non-failed status) →
 *   auto-clear: entries on the same printer target lose the printed order
 *   ids (a group entry shrinks per-order and disappears when its last
 *   member prints); id-less entries clear on any same-target success.
 */
export function applyPrintJobToFailures(
  entries: IPrintFailureEntry[],
  job: IPrintJob,
  now: number
): IPrintFailureEntry[] {
  if (job.status !== 'printed') {
    const key = failureKeyOf(job);
    const kind =
      job.status === 'unknown' ? 'verify' : failureKindOf(job.retrySafety);
    const existing = entries.find((entry) => entry.key === key);

    if (existing) {
      const updated: IPrintFailureEntry = {
        ...existing,
        kind: existing.kind === 'verify' ? 'verify' : kind,
        errorMessage: job.errorMessage ?? existing.errorMessage,
        count: existing.count + 1,
        lastAt: now,
      };
      return entries.map((entry) => (entry.key === key ? updated : entry));
    }

    const created: IPrintFailureEntry = {
      key,
      kind,
      printerType: job.printerType,
      comandaStationId: job.comandaStationId,
      orderIds: orderIdsOf(job),
      title: job.title,
      errorMessage: job.errorMessage,
      count: 1,
      firstAt: now,
      lastAt: now,
    };
    return [...entries, created];
  }

  // Success: clear/shrink matching entries.
  const printedIds = new Set(orderIdsOf(job));
  const next: IPrintFailureEntry[] = [];
  for (const entry of entries) {
    if (!sameTarget(entry, job)) {
      next.push(entry);
      continue;
    }
    if (!entry.orderIds.length) {
      // Id-less entry (title-keyed): any same-target success clears it.
      continue;
    }
    if (!printedIds.size) {
      // Success without order ids (e.g. an order-less label tool print)
      // can't be matched to specific orders — keep the entry.
      next.push(entry);
      continue;
    }
    const remaining = entry.orderIds.filter((id) => !printedIds.has(id));
    if (remaining.length === entry.orderIds.length) {
      next.push(entry);
    } else if (remaining.length) {
      next.push({ ...entry, orderIds: remaining });
    }
    // else: fully covered — drop the entry.
  }
  return next;
}

/** Drops acked keys whose entries no longer exist (so a re-failure re-arms). */
export function pruneAckedKeys(
  ackedKeys: string[],
  entries: IPrintFailureEntry[]
): string[] {
  const live = new Set(entries.map((entry) => entry.key));
  return ackedKeys.filter((key) => live.has(key));
}

/**
 * Bridge status → unified status. Bridge 'error' (reachable but paper-out /
 * fault) maps to 'offline': the amber warning must show, and an
 * error → online transition fires the recovery reconcile just like
 * offline → online (paper refill IS the incident class).
 */
export function mapBridgeStatusToUnified(
  status: BridgePrinterStatus
): UnifiedPrinterStatus {
  if (status === 'online') return 'online';
  if (status === 'unknown') return 'unknown';
  return 'offline';
}

/**
 * Keys that recovered: offline → online ONLY. unknown → online never fires
 * (first poll after startup must not trigger a reconcile).
 */
export function diffRecoveredPrinterKeys(
  prev: Record<string, UnifiedPrinterStatus>,
  next: Record<string, UnifiedPrinterStatus>
): string[] {
  return Object.keys(next).filter(
    (key) => next[key] === 'online' && prev[key] === 'offline'
  );
}

/**
 * DEMORADA banner age: whole minutes since the OLDEST item being printed.
 * Each item is anchored on the NEWEST of modifiedAt / addedAt — a quantity
 * bump on an old order refreshes modifiedAt, so "one more beer" 20 minutes
 * in must not read as a 20-minute-late comanda. Items with neither stamp
 * (original v1 creations) fall back to order.createdAt.
 * Returns null when the oldest item is under the threshold.
 */
export function delayedPrintMinutesOf(params: {
  items: { addedAt?: string | null; modifiedAt?: string | null }[];
  orderCreatedAt: string | Date;
  now: number;
}): number | null {
  let oldestAt: number | null = null;
  const orderCreatedMs = new Date(params.orderCreatedAt).getTime();

  for (const item of params.items) {
    const stampsMs = [item.addedAt, item.modifiedAt]
      .filter((stamp): stamp is string => !!stamp)
      .map((stamp) => new Date(stamp).getTime())
      .filter((ms) => !Number.isNaN(ms));
    const anchorMs = stampsMs.length ? Math.max(...stampsMs) : orderCreatedMs;
    if (Number.isNaN(anchorMs)) continue;
    if (oldestAt === null || anchorMs < oldestAt) oldestAt = anchorMs;
  }

  if (oldestAt === null) return null;
  const ageMs = params.now - oldestAt;
  if (ageMs <= DELAYED_PRINT_THRESHOLD_MS) return null;
  return Math.floor(ageMs / 60_000);
}
